import { createFileRoute } from "@tanstack/react-router";
import { loadStandalonePage } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";
import { Button } from "~/components/Button";
import { CTACenteredButtons } from "~/components/blocks/CTACenteredButtons";
import { AboutVisionMark } from "~/components/graphics/AboutVisionMark";

export const Route = createFileRoute("/about-us")({
  loader: async () => {
    const data = await loadStandalonePage({
      data: { path: "about-us/_index.md" },
    });
    return {
      title: (data?.title as string) || "About Us",
      seo_title: (data?.seo_title as string) || "About Us - C1",
      seo_description: data?.seo_description as string | undefined,
      hero: data?.hero as any,
      about: data?.about as any,
      vision: data?.vision as any,
      leadership: data?.leadership as any,
      logos: data?.logos as any,
      values: data?.values as any,
      team: data?.team as any,
      locations: data?.locations as any,
      cta: data?.cta as any,
    };
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.seo_title,
          description: loaderData.seo_description,
          url: "/about-us",
        })
      : [],
    links: seoLinks({ url: "/about-us" }),
  }),
  component: AboutUs,
});

/** Empty tiles the team rail falls back to when `team.images` is absent — enough to
 *  overflow at every width so the rail still reads as scrollable. Once images are
 *  supplied the rail renders exactly as many tiles as there are photos, so adding a
 *  tenth is a front-matter edit and nothing else. */
const TEAM_RAIL_PLACEHOLDERS = 8;

/** Every investor SVG is authored 32px tall, so the default max-h-8 just caps them at
 *  their natural size. Fathom's asset is not a bare wordmark like the rest but a boxed
 *  lockup — a dark plate with a rule, holding "Fathom." above a tagline — so its
 *  wordmark is only 22% of the asset height and rendered at ~7px, far smaller than its
 *  neighbours. Scaling the whole plate is the only lever available, since the plate is
 *  part of the mark and cropping it would alter the logo.
 *
 *  These have to be explicit heights, not max-heights: max-h-* only ever constrains,
 *  so against a 32px-tall source a larger max-h is a no-op.
 *
 *  h-14 puts Fathom's wordmark at ~12.6px, which reads level with the neighbouring
 *  wordmarks; h-12 left it noticeably smaller. The plate fits its cell at every
 *  breakpoint with room to spare, so it can go larger if it wants more presence.
 *  Note the cell's own max-w-full will clamp this if the cell gets too narrow —
 *  which is why cell padding drops below lg. */
const LOGO_HEIGHT: Record<string, string> = {
  "/images/investor-fathom.svg": "h-14",
};
const LOGO_HEIGHT_DEFAULT = "max-h-8";

/** Section headings ramp `text-33 md:text-40 lg:<desktop size>` — the step "Where we
 *  work" already used, and the house pattern across the block library. Flat at their
 *  desktop size they were the largest type on a phone: the hero h1 ramps down to 28px,
 *  so a 48px section label outsized the page title by 1.75x and "Our leadership team"
 *  took three lines.
 *
 *  Tailwind v4 gotcha, and the reason the CTA below carries `lg:tracking-normal`: a
 *  font-size step only resets leading/tracking if that size has tokens of its own.
 *  `text-33`, `text-40` and `text-48` do; `text-49` and every arbitrary `text-[…]` do
 *  not. So an arbitrary desktop size under a ramp inherits the smaller step's metrics
 *  unless the reset is spelled out. The h2s here are safe because they set
 *  `leading-tight tracking-tight` explicitly at every width. */

function AboutUs() {
  const { hero, about, vision, leadership, logos, values, team, locations, cta } =
    Route.useLoaderData();

  return (
    <div>
      {/* Hero. Layout mirrors the homepage hero (HomeHero2026.tsx): white background,
          max-w-screen-xl container, fixed lg:h-[544px], and a lg:w-1/2 copy column
          beside a lg:flex-1 visual, stacking below lg. Only this section is white —
          the rest of the page keeps the cream page background.

          home-rise is the same fade-and-rise the homepage hero uses (defined in
          app.css, staggered by inline animation-delay in 80ms steps). Reusing the
          class rather than copying the keyframes keeps the motion identical and
          inherits its accessibility behaviour: the rule lives entirely inside a
          prefers-reduced-motion: no-preference query, so under reduced motion nothing
          animates and the content just shows. animation-fill-mode both holds the
          hidden start state at first paint, so there is no flash of unstyled content
          before it plays. Order follows the visual stack. */}
      {hero && (
        <div className="relative z-[1] overflow-clip bg-white px-4 md:px-8 lg:h-[544px]">
          <div className="mx-auto max-w-screen-xl lg:h-full">
            <div className="flex flex-col lg:h-full lg:flex-row lg:items-center">
              {/* Below lg the mark stacks under this column and carries the section's
                  bottom padding, so what is left here is the copy-to-mark gap. */}
              <div className="pb-8 pt-10 md:pb-10 md:pt-16 lg:w-1/2 lg:pb-4 lg:pr-14 lg:pt-4">
                {hero.label && (
                  <div
                    className="home-rise mb-5 flex md:mb-12 lg:mb-14"
                    style={{ animationDelay: "0ms" }}
                  >
                    <span className="chip bg-teal-dark text-white">
                      {hero.label}
                    </span>
                  </div>
                )}
                {/* Weight and the md-and-up steps are the homepage hero's
                    (HomeHero2026.tsx): 40 / 48 / 67.3px. The phone step deliberately
                    is not. The homepage title is a full line of copy, so at 28px it
                    still fills the measure; "Meet C1" is two short words and reads
                    undersized at the same value against the rest of the fold. Holding
                    40px down to the smallest width gives it comparable presence —
                    ~150px of a 342px measure at 390px, so it stays on one line, and
                    it still clears the 33px section headings below it. */}
                <h1
                  className="home-rise font-display text-40 font-bold text-nav-text lg:text-48 xl:text-[67.3px] xl:leading-[90.9px]"
                  style={{ animationDelay: "80ms" }}
                >
                  {hero.heading}
                </h1>
                {/* mt-4 flat, matching the homepage hero's title-to-subhead distance.
                    The h1 carries no bottom margin on either page, so this margin *is*
                    the gap: 16px at every breakpoint, where this previously ramped
                    12/20/32px.

                    max-w-md wraps the copy on phones; unconstrained from md up so it
                    sits on one line where the column is wide enough for its natural
                    505px. In the 1040-1190px band the half column is narrower than
                    that, so it takes two lines there. */}
                {hero.copy && (
                  <p
                    className="home-rise mt-4 max-w-md font-body text-[1.33rem] font-normal leading-relaxed text-nav-text md:max-w-none"
                    style={{ animationDelay: "160ms" }}
                  >
                    {hero.copy}
                  </p>
                )}
              </div>
              {/* The animated C1 mark. Unlike the homepage — which drops its hero
                  visual below lg because the asset ate most of the fold there — this
                  one shows at every width, stacking under the subhead and scaling in
                  proportion. Below lg the box is the mark's own 344/302, capped at
                  28rem so it stays a hero visual on tablets instead of a 618px-tall
                  slab; from lg up it loses the ratio and stretches to fill the hero's
                  fixed 544px beside the copy.

                  A self-contained page in an iframe, the same way the homepage CTA
                  carries its dither animation. It has to live under /marketecture/ (or
                  /embeds//animations/): every other path is served frame-ancestors
                  'none' + X-Frame-Options DENY by vercel-headers.json, so an iframe
                  anywhere else is refused.

                  items-start / lg:items-stretch is what switches those two modes: the
                  default stretch would override the aspect ratio below lg, and at lg it
                  is what gives the inner box its height. Deliberately stretch rather
                  than h-full — a percentage height against a stretched flex item is the
                  case Safari resolves to zero. For the same reason the iframe is
                  absolutely positioned into the box rather than sized h-full: inset-0
                  needs no percentage to resolve.

                  No pointer-events-none here, unlike the CTA embed — this page steers
                  the mark from its own pointerenter/move/leave handlers, so it needs
                  the events. It gates its render loop on an IntersectionObserver,
                  document.hidden and prefers-reduced-motion. Decorative, hence
                  aria-hidden — and tabIndex={-1} because an iframe is in the tab order
                  by default, so without it a keyboard user lands on a frame the
                  accessibility tree says is not there (axe aria-hidden-focus).

                  Delay 240ms: it now renders after the subhead in source order below
                  lg, so it has to rise last or it beats the copy above it. */}
              <div
                aria-hidden="true"
                className="home-rise flex items-start justify-center pb-10 md:pb-16 lg:flex-1 lg:items-stretch lg:self-stretch lg:pb-0"
                style={{ animationDelay: "240ms" }}
              >
                <div className="relative aspect-[344/302] w-full max-w-[28rem] lg:aspect-auto lg:max-w-none">
                  <iframe
                    src="/marketecture/about-hero-c1mark.html"
                    title=""
                    aria-hidden="true"
                    tabIndex={-1}
                    scrolling="no"
                    className="absolute inset-0 h-full w-full rounded-20 border-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Founded in Security + Vision — one section, on the page's own light
          background so the page reads bright all the way down to the CTA. Padding is
          the site's standard section rhythm. */}
      {(about || vision) && (
        <div className="relative overflow-hidden px-4 pb-16 pt-16 md:px-8 md:pb-24 md:pt-24 lg:pb-[7.5rem] lg:pt-[7.5rem]">
          <div className="relative mx-auto max-w-screen-xl">
            {/* Founders: heading and the two portraits share the left column, copy sits
                in the right one. The 647px column and 56px gutter carry over from the
                Vision row below, so both rows keep the same measure.

                The portrait tiles reuse the treatment from the "Who's in the room"
                speaker grid on /c1-transform-2026 (TransformSpeakers.tsx): 4:5 box,
                square corners, name in font-display bold, title in body gray-80
                beneath. Same convention for placeholders too — a person with no
                `image` renders as an empty tile, aria-hidden so a screen reader is not
                told about a picture that is not there. */}
            {about && (
              /* Three explicit grid cells rather than two nested columns: heading in
                 row 1, portraits and copy sharing row 2. That is what puts the copy's
                 top edge level with the top of the portraits — a top margin on the copy
                 would have to guess the heading's rendered height and would drift the
                 moment it wraps or the type scale changes. Row gap replaces the margin
                 that used to sit above the portraits. */
              <div className="grid items-start gap-x-9 gap-y-8 md:gap-y-10 lg:grid-cols-[647px_1fr] lg:gap-x-14">
                {about.heading && (
                  <h2 className="font-display text-33 md:text-40 lg:text-[3rem] font-semibold leading-tight text-gray-90 tracking-tight lg:col-start-1 lg:row-start-1">
                    {about.heading}
                  </h2>
                )}
                <div className="lg:col-start-1 lg:row-start-2">
                  {/* Two up, but stacked below 360px: at 328px of content the pair is
                      152px each, narrower than the name/title beneath them wants. The
                      leadership grid takes the same 360px floor and the same gap-6, so
                      the two blocks step together. */}
                  {about.people && (
                    <div className="grid grid-cols-1 gap-6 min-[360px]:grid-cols-2">
                      {(about.people as any[]).map((person: any, i: number) => (
                        <div key={i}>
                          <div
                            className="aspect-[4/5] w-full overflow-hidden bg-teal-dark"
                            aria-hidden={person.image ? undefined : true}
                          >
                            {person.image && (
                              <img
                                src={person.image}
                                alt={person.image_alt || person.name || ""}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            )}
                          </div>
                          <div className="mt-4 md:mt-5">
                            {person.name && (
                              <p className="font-display text-base font-bold text-charcoal md:text-19">
                                {person.name}
                              </p>
                            )}
                            {person.title && (
                              <p className="mt-1 font-body text-sm text-gray-80 md:text-base">
                                {person.title}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Centred against the photos, not against the column. The column is
                    taller than the photos by the name/title block, so plain self-center
                    would sit the copy ~38px low. Instead the cell is given exactly the
                    photo height and centres its content inside that: the tiles are
                    (647px column - 1.5rem gap) / 2 wide at 4:5, so 1.25x that. The
                    constants are the same ones the grid template uses above.

                    min-h rather than h: between lg and ~1190px the copy column is narrow
                    enough that the text runs taller than the photos, and a fixed height
                    made it overflow upward into the heading. With min-h the cell grows
                    instead, and the centring only applies when there is room. */}
                <div className="lg:col-start-2 lg:row-start-2 lg:flex lg:min-h-[calc((647px-1.5rem)/2*1.25)] lg:items-center lg:self-start">
                  <div>
                    {about.copy &&
                      String(about.copy)
                        .split(/\n+/)
                        .filter((para: string) => para.trim())
                        .map((para: string, i: number) => (
                          <p
                            key={i}
                            className={`font-body text-lg leading-[1.87] text-gray-80 tracking-tight ${i === 0 ? "" : "mt-5"}`}
                          >
                            {para}
                          </p>
                        ))}
                  </div>
                </div>
              </div>
            )}

            {/* Vision shares this section. The top margin used to reproduce the gap
                from when the two were separate sections (144/192/232/248px); it is now
                deliberately tighter so the two rows read as one block. */}
            {/* Vision mirrors the Founders row above: same 647px image column, same
                56px gutter, same items-center, same aspect-[647/414] object-cover. The
                column order is the mirror image — copy left, photo right — so the two
                rows alternate rather than repeat. Both source images are aspect 1.45
                against the 1.56 target, so each takes the same slight crop.

                The Vision frame is the source's own 1200x826 rather than the Founders
                photo's 647x414. That makes it taller — 647x445 instead of 647x414 — and
                it is also the tallest this can go at this width without object-cover
                starting to eat the sides, where the artwork has a pink node close to the
                left edge and the card runs to the right. As a bonus the 7% top/bottom
                crop the 647x414 frame imposed is gone, so the whole graphic shows. */}
            {vision && (
              <div className="grid items-center gap-9 lg:grid-cols-[1fr_647px] lg:gap-14 mt-20 md:mt-24 lg:mt-28 xl:mt-32">
                <div>
                  {vision.heading && (
                    <h2 className="font-display text-33 md:text-40 lg:text-[3rem] font-semibold leading-tight text-gray-90 tracking-tight">
                      {vision.heading}
                    </h2>
                  )}
                  {vision.copy && (
                    <p className="mt-5 font-body text-lg leading-[1.87] text-gray-80 tracking-tight">
                      {vision.copy}
                    </p>
                  )}
                </div>
                <div>
                  {/* Native port of the old /marketecture/about-vision-anim.html
                      iframe (RFC 0003 Axis 1). The export's own trailing comment
                      claimed c1-embed.js supplied the off-screen pause, but the file
                      never loaded the runtime — its six infinite keyframes ran for
                      the whole session. The component gates itself via
                      IntersectionObserver, so no iframe, no mount gate, no guest
                      document; the SVG artwork is carried verbatim from the export.
                      Decorative, hence aria-hidden inside the component. */}
                  <AboutVisionMark />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Leadership */}
      {leadership?.cards && (
        <div className="px-4 pb-20 pt-16 md:px-8 md:pt-20 lg:pt-24 xl:pt-28">
          <div className="mx-auto max-w-screen-xl">
            {leadership.heading && (
              <h2 className="font-display text-33 md:text-40 lg:text-[3rem] font-semibold leading-tight text-gray-90 tracking-tight">
                {leadership.heading}
              </h2>
            )}
            {/* A real grid rather than flex-wrap with width calcs. Two reasons: the
                calcs subtracted a whole gap per item instead of the item's share of
                it — (100% - (n-1)*gap)/n — so every row came up 1.5rem short and, with
                xl:justify-start, the last column stopped 24px inside the container and
                out of line with the investor grid below; and a single grid-cols-* ramp
                is what makes the step down to two-up and then to stacked legible.

                Steps: stacked below 360px (at 312px of content a pair is 144px each,
                too narrow for the name and title beneath), two up from 360px, three at
                lg, four at xl. The 360px floor and gap-6 match the founders grid so the
                two blocks move together. */}
            <div className="mt-8 grid grid-cols-1 gap-6 min-[360px]:grid-cols-2 md:mt-16 lg:grid-cols-3 xl:grid-cols-4">
              {/* No card: the photo fills the full grid-item width (what the card
                  measured before), with the name/title kept underneath. */}
              {(leadership.cards as any[]).map((person: any, i: number) => (
                <div
                  key={i}
                  className="group relative transition-transform duration-300 hover:-translate-y-1"
                >
                  {/* The frame holds the 4:5 ratio and the image sits at the bottom
                      of it, scaled to 82% of the height. Because the headshots' own
                      backdrop is exactly #FFBFF4, the frame colour behind the
                      remaining 18% reads as continuous headroom above the subject
                      rather than as letterboxing. */}
                  {person.image && (
                    <div className="flex aspect-[4/5] w-full items-end overflow-hidden bg-[#FFBFF4]">
                      {/* alt="" deliberately: the name is rendered as text directly
                          beneath, so a descriptive alt makes a screen reader announce
                          it twice. The photo carries nothing the text does not. */}
                      <img
                        src={person.image}
                        alt=""
                        className={`w-full object-cover object-top ${
                          person.fills_frame ? "h-full" : "h-[82%]"
                        }`}
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="mt-3 flex items-start justify-between">
                    <div>
                      {person.name && (
                        <h3 className="font-bold text-gray-90">{person.name}</h3>
                      )}
                      {person.title && (
                        <p className="mt-1 text-gray-80">{person.title}</p>
                      )}
                    </div>
                    {person.linkedin && (
                      <svg
                        aria-hidden="true"
                        focusable="false"
                        className="mt-1 h-[18px] w-[18px] flex-none text-gray-60 transition-all duration-300 group-hover:text-gray-90"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M0 1.32C0 .612.596.036 1.33.036h15.34c.735 0 1.33.576 1.33 1.284v15.36c0 .709-.595 1.284-1.33 1.284H1.33C.596 17.964 0 17.389 0 16.68V1.32Z"
                          fill="currentColor"
                        />
                        <path
                          d="M5.47 15.039V6.968H2.776v8.071H5.47Zm-1.346-9.173c.94 0 1.524-.62 1.524-1.395-.018-.792-.585-1.394-1.506-1.394-.921 0-1.524.602-1.524 1.394 0 .775.585 1.395 1.488 1.395h.018ZM6.96 15.039h2.694v-4.507c0-.241.018-.482.089-.655.194-.482.638-.981 1.382-.981.974 0 1.364.74 1.364 1.826v4.317h2.693v-4.628c0-2.479-1.328-3.633-3.1-3.633-1.453 0-2.091.81-2.446 1.36h.018V6.968H6.96c.035.757 0 8.071 0 8.071Z"
                          fill="white"
                        />
                      </svg>
                    )}
                  </div>
                  {/* The whole card is the link, so the anchor is an empty overlay —
                      which means it has no text to take its accessible name from, and
                      a screen reader announced twelve links all called "link" (axe
                      link-name). The visible name sits in a sibling, so it has to be
                      spelled out here. */}
                  {person.linkedin && (
                    <a
                      href={person.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${person.name} on LinkedIn`}
                      className="absolute inset-0"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Investor Logos */}
      {logos?.images && (
        <div className="px-4 md:px-8">
          <div className="mx-auto max-w-screen-xl">
            <div className="pb-16 pt-6 md:pb-20">
              {logos.heading && (
                <div className="flex justify-center">
                  <span className="chip bg-pink-accent text-nav-text">
                    {logos.heading}
                  </span>
                </div>
              )}
              {/* One grid of equal rectangles, each holding a single logo. Every
                  cell carries a right and bottom border and the container supplies
                  the outer left and top, so adjacent cells share one hairline
                  instead of doubling it up. The 10 logos divide evenly into both
                  2 and 5 columns, so neither breakpoint leaves a ragged row. */}
              <div className="mt-5 grid grid-cols-2 border-l border-t border-gray-30 md:mt-6 md:grid-cols-5">
                {(logos.images as string[]).map((src: string, i: number) => (
                  <div
                    key={i}
                    className="flex h-24 items-center justify-center border-b border-r border-gray-30 bg-white px-4 md:h-28 lg:px-8"
                  >
                    <img
                      src={src}
                      alt=""
                      className={`${LOGO_HEIGHT[src] ?? LOGO_HEIGHT_DEFAULT} w-auto max-w-full object-contain`}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Values */}
      {values?.cards && (
        <div className="px-4 pb-24 pt-20 md:px-8">
          <div className="mx-auto max-w-screen-xl">
            {values.heading && (
              <h2 className="font-display text-33 md:text-40 lg:text-[3rem] font-semibold leading-tight text-gray-90 tracking-tight">
                {values.heading}
              </h2>
            )}
            {/* No cards. Mirrors the stats band under the homepage hero
                (home2026/StatsSection.tsx): a divided grid that stacks with
                horizontal rules below lg and becomes columns with vertical rules
                from lg up, using the same first/last/middle padding recipe.
                divide-gray-30 is the light-mode counterpart to that section's
                divide-white/15. Everything is left-aligned, icons included, so the
                centering wrapper around the icon chip is gone too. */}
            <div className="mt-16 grid grid-cols-1 divide-y divide-gray-30 lg:grid-cols-4 lg:divide-x lg:divide-y-0">
              {(values.cards as any[]).map((card: any, i: number) => (
                <div
                  key={i}
                  className={`flex flex-col py-10 lg:py-4 ${
                    i === 0
                      ? "lg:pl-0 lg:pr-8"
                      : i === (values.cards as any[]).length - 1
                        ? "lg:pl-8 lg:pr-0"
                        : "lg:px-8"
                  }`}
                >
                  {/* No brightness-0 invert here any more: that existed to force the
                      old multi-colour fills to white. The current mark is already
                      stroked in pink-accent, which is what should read on the teal
                      chip. */}
                  {card.icon && (
                    <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-teal-dark">
                      <img
                        src={`/images/svg/${card.icon}`}
                        alt=""
                        className="h-9 w-9"
                      />
                    </div>
                  )}
                  {card.heading && (
                    <h3 className="mt-5 text-19 font-semibold text-gray-90">
                      {card.heading}
                    </h3>
                  )}
                  {card.copy && (
                    <p className="mt-3 text-gray-80">{card.copy}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Team.

          No horizontal padding on the section: the rail below has to reach the
          viewport's right edge, and it cannot do that from inside a padded parent.
          The heading block carries the page gutter itself instead, and the rail folds
          the same gutter into its own scroll padding. */}
      {team && (
        <div className="relative pb-16 pt-20 md:pb-20 md:pt-24 lg:pt-16 xl:pt-10">
          <div className="px-4 md:px-8">
            <div className="relative mx-auto max-w-screen-xl">
              <div className="max-w-xl">
                {team.heading && (
                  <h2 className="font-display text-33 md:text-40 lg:text-[3rem] font-semibold leading-tight text-gray-90 tracking-tight">
                    {team.heading}
                  </h2>
                )}
                {team.copy && (
                  <p className="mt-4 text-gray-80">{team.copy}</p>
                )}
                {team.button && (
                  <div className="mt-9">
                    <Button href={team.button.url} variant="teal">
                      {team.button.text}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Team rail. This is the one rail of its kind left on the site — the
              speakers block on /c1-transform-2026 that it was modelled on is a
              wrapping grid now. `no-scrollbar` hides the bar, so `snap-x
              snap-mandatory` + `snap-start` is what lands each tile flush instead of
              a flick coasting to an arbitrary offset, `overflow-y-hidden` stops a
              diagonal swipe panning the page, and `tabIndex` keeps the off-screen
              tiles keyboard-reachable (WCAG 2.1.1). `scroll-pl-*` mirrors `pl-*` so
              the first tile rests in line with the heading rather than against the
              viewport edge.

              The rail owns the page gutter itself now, because its parent dropped
              `px-*` so the scrollport could reach the right edge of the screen — the
              row reads as continuing off-screen rather than as a short list that
              happens to stop inside the margin. `pr-*` stays as scroll-end padding:
              scrolled to the end, the last tile rests a gutter in from the edge
              rather than flush against it.

              The `pl-*` expression rebuilds where the container's inner edge lands on
              this page. Beware the other form of it — `max(0, (100% - container)/2)
              + gutter` — which is correct only for a section that puts its padding
              INSIDE `max-w-screen-xl` (`mx-auto max-w-screen-xl px-4`). Every section
              on this page pads outside it, so the gutter comes off the viewport first
              and the container centres in what is left:
              `gutter + max(0, (100% - 2*gutter - container)/2)`. The inside-the-
              container form put the first tile a full gutter right of the heading
              above 1344px.

              The rail renders one tile per `team.images` entry, falling back to
              TEAM_RAIL_PLACEHOLDERS empty tiles when the list is absent — so adding
              or removing a photo is a front-matter edit and nothing else. */}
          <div
            className="no-scrollbar mt-12 flex snap-x snap-mandatory gap-6 overflow-x-auto overflow-y-hidden overscroll-x-contain pl-[calc(1rem_+_max(0px,(100%_-_2rem_-_var(--container-screen-xl))/2))] pr-4 scroll-pl-[calc(1rem_+_max(0px,(100%_-_2rem_-_var(--container-screen-xl))/2))] md:mt-14 md:pl-[calc(2rem_+_max(0px,(100%_-_4rem_-_var(--container-screen-xl))/2))] md:pr-8 md:scroll-pl-[calc(2rem_+_max(0px,(100%_-_4rem_-_var(--container-screen-xl))/2))]"
            role="region"
            aria-label={team.heading ? String(team.heading) : "Our team"}
            tabIndex={0}
          >
            {Array.from({
              length:
                (team.images as unknown[] | undefined)?.length ||
                TEAM_RAIL_PLACEHOLDERS,
            }).map((_, i) => {
              const img = (team.images as { src?: string; alt?: string }[] | undefined)?.[i];
              return (
                <div
                  key={i}
                  className="w-[260px] flex-none snap-start sm:w-[284px] md:w-[288px] lg:w-[311.5px]"
                >
                  <div
                    className="aspect-[4/5] w-full overflow-hidden bg-gray-30"
                    aria-hidden={img?.src ? undefined : true}
                  >
                    {img?.src && (
                      <img
                        src={img.src}
                        alt={img.alt || ""}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      )}

      {/* Locations — no container; a rule separates it from the section above and
          the type takes the same colours as the other light sections. The pin icons
          were fill="white" for the old teal box, so they now inherit currentColor. */}
      {locations?.cards && (
        <div className="px-4 pb-16 md:px-8 md:pb-20">
          <div className="mx-auto max-w-screen-xl border-t border-gray-30 pt-10 md:pt-12">
            <div className="flex flex-wrap lg:items-center">
              <div className="flex w-full items-center md:w-2/5 lg:w-[30%] xl:w-[36%]">
                {locations.heading && (
                  <h2 className="mb-7 font-display text-33 font-semibold leading-none text-gray-90 md:mb-0 md:max-w-[13rem] lg:text-[2.5rem] xl:max-w-none">
                    {locations.heading}
                  </h2>
                )}
              </div>
              {(locations.cards as any[]).map((loc: any, i: number) => (
                <div
                  key={i}
                  /* Full width below sm: at 390px a half column minus pr-10 left
                     ~131px of measure, which broke "1300 SE Stark Street, Suite 106"
                     over three lines. Two up from sm, where half the column is wide
                     enough for the street line. md and up is unchanged. */
                  className="mt-6 w-full pr-0 sm:w-1/2 sm:pr-6 md:mt-0 md:w-[30%] md:pr-10 lg:w-[30%]"
                >
                  <svg
                    className="h-5 w-5 text-teal-dark lg:h-6 lg:w-6"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M8.668 10c-1.1 0-2-0.9-2-2s0.9-2 2-2 2 0.9 2 2-0.9 2-2 2Zm6-1.8c0-3.63-2.65-6.2-6-6.2s-6 2.57-6 6.2c0 2.34 1.95 5.44 6 9.14 4.05-3.7 6-6.8 6-9.14ZM8.668 0c4.2 0 8 3.22 8 8.2 0 3.32-2.67 7.25-8 11.8C3.338 15.45.668 11.52.668 8.2c0-4.98 3.8-8.2 8-8.2Z"
                      fill="currentColor"
                    />
                  </svg>
                  {loc.heading && (
                    <h3 className="mt-2.5 text-11 font-semibold uppercase tracking-widest-4 text-gray-90">
                      {loc.heading}
                    </h3>
                  )}
                  {loc.copy && (
                    <p
                      className="mt-2 font-medium leading-snug text-gray-80"
                      dangerouslySetInnerHTML={{ __html: loc.copy }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA — the same cta-centered-buttons block the homepage closes with, so the
          dark panel, dither-animation background and button treatment match. Only
          this page's own heading and button are passed through. heading_class
          mirrors what the homepage block sets in its front matter; without it the
          component's larger default would render at a different size to the
          homepage.

          The lg: pair is that homepage-matching size; the steps below it are this
          page's own. Flat at 50.2px, "Adopt AI, fearlessly." set two lines wider
          than the dither shape behind it on a phone, so the type overhung its own
          backdrop. The leading is scoped to lg with the size it belongs to —
          text-33 and text-40 carry their own 44/51px, where a flat 68px would leave
          a nearly double-spaced line box around 33px type. lg:tracking-normal is a
          reset rather than styling: text-[50.2px] is an arbitrary value and carries
          no letter-spacing of its own, so without it text-40's -1% rides up into the
          desktop size. */}
      {cta && (
        <CTACenteredButtons
          heading={cta.heading}
          heading_class={
            cta.heading_class ||
            "text-center font-display font-bold text-white text-33 md:text-40 lg:text-[50.2px] lg:leading-[68px] lg:tracking-normal"
          }
          button_primary={cta.button}
        />
      )}
    </div>
  );
}
